import type { World } from '../sim/world';
import { WEATHER_KINDS, type WeatherKind } from '../sim/weather';
import { SAIL_PLANS } from '../data/ship';
import { CAMERA_MODES, type CameraMode } from '../render/cameras';
import { QUALITY } from '../render/view';
import { wrapAngle } from '../core/math';
import { CombatHud } from './combatHud';
import { Tracker } from './tracking';
import type { SimEvent } from '../sim/events';
import type { DifficultyId } from '../data/classes';
import type { GroupKind } from '../sim/encounters';

// Heads-up display, settings panel, key help and the title card. Reads sim
// state; changes go back through the callbacks the app provides.

export interface HudActions {
  setWeather(k: WeatherKind): void;
  setDynamicWeather(on: boolean): void;
  setHours(h: number): void;
  setTimeScale(s: number): void;
  setGameSpeed(s: number): void;
  setWaveIntensity(v: number): void;
  setWind(speed: number | null): void;
  setQuality(k: string): void;
  setPost(key: 'bloom' | 'dof' | 'motionBlur' | 'godRays' | 'grain', on: boolean): void;
  setVolume(v: number): void;
  setCamera(m: CameraMode): void;
  setSensitivity(v: number): void;
  setEnemies(on: boolean): void;
  setDifficulty(d: DifficultyId): void;
  spawn(kind: GroupKind): void;
  restart(): void;
  /** A ship was picked (or dropped, -1) for tracking. */
  track(id: number): void;
  start(): void;
}

const WEATHER_NAMES: Record<WeatherKind, string> = {
  clear: 'Clear', fair: 'Fair', overcast: 'Overcast', fog: 'Sea fog', rain: 'Rain', storm: 'Storm',
};

const BEAUFORT = [0.5, 1.6, 3.4, 5.5, 8, 10.8, 13.9, 17.2, 20.8, 24.5, 28.5, 32.7];
const BEAUFORT_NAMES = ['Calm', 'Light air', 'Light breeze', 'Gentle breeze', 'Moderate breeze', 'Fresh breeze', 'Strong breeze', 'Near gale', 'Gale', 'Strong gale', 'Storm', 'Violent storm', 'Hurricane'];

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, html?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (html !== undefined) e.innerHTML = html;
  return e;
}

export class Hud {
  private readonly title: HTMLElement;
  private readonly status: HTMLElement;
  private readonly compass: HTMLElement;
  private readonly compassTape: HTMLElement;
  private readonly speed: HTMLElement;
  private readonly heel: HTMLElement;
  private readonly windArrow: SVGElement;
  private readonly appArrow: SVGElement;
  private readonly windText: HTMLElement;
  private readonly sailName: HTMLElement;
  private readonly sailBars: HTMLElement[] = [];
  private readonly rudder: HTMLElement;
  private readonly trim: HTMLElement;
  readonly combat: CombatHud;
  /** Keeping a glass on one ship from the In Sight list. */
  readonly tracker: Tracker;
  private readonly clock: HTMLElement;
  private readonly weather: HTMLElement;
  private readonly camName: HTMLElement;
  private readonly shotName: HTMLElement;
  private readonly fps: HTMLElement;
  private readonly help: HTMLElement;
  private readonly panel: HTMLElement;
  private readonly hudWrap: HTMLElement;
  private readonly controls: Record<string, HTMLInputElement | HTMLSelectElement> = {};
  private hidden = false;

  constructor(root: HTMLElement, private readonly world: World, private readonly actions: HudActions) {
    this.hudWrap = el('div', 'hud');
    root.appendChild(this.hudWrap);

    // --- title card
    this.title = el('div', 'title');
    this.title.innerHTML = `
      <div class="title-inner">
        <div class="title-kicker">A sailing simulation</div>
        <h1>Tidebreaker</h1>
        <p class="title-sub">Three masts, twenty guns, a great deal of sea — and those who would take her.</p>
        <div class="title-status">Preparing the sea…</div>
        <button class="title-go" disabled>Set sail</button>
        <div class="title-keys">
          <span><b>A D</b> helm</span><span><b>W S</b> make / shorten sail</span><span><b>Q E</b> fire broadside</span>
          <span><b>G</b> shot</span><span><b>B</b> board</span><span><b>8</b> battle view</span>
          <span><b>1–7</b> cameras</span><span><b>Tab</b> weather, enemies &amp; settings</span><span><b>H</b> help</span>
        </div>
      </div>`;
    root.appendChild(this.title);
    this.status = this.title.querySelector('.title-status')!;
    const go = this.title.querySelector('.title-go') as HTMLButtonElement;
    go.addEventListener('click', () => {
      this.title.classList.add('gone');
      actions.start();
    });

    // --- helm panel (bottom left)
    const helm = el('div', 'panel helm');
    this.compass = el('div', 'compass');
    this.compassTape = el('div', 'compass-tape');
    this.compass.appendChild(this.compassTape);
    this.compass.appendChild(el('div', 'compass-mark'));
    for (let d = -360; d <= 720; d += 15) {
      const t = el('span', d % 90 === 0 ? 'cardinal' : d % 45 === 0 ? 'inter' : 'tick');
      const deg = ((d % 360) + 360) % 360;
      t.textContent = d % 90 === 0 ? ['N', 'E', 'S', 'W'][deg / 90]! : d % 45 === 0 ? ['NE', 'SE', 'SW', 'NW'][(deg - 45) / 90]! : String(deg).padStart(3, '0');
      t.style.left = `${(d + 360) * 3}px`;
      this.compassTape.appendChild(t);
    }
    helm.appendChild(this.compass);
    const row = el('div', 'helm-row');
    const speedBox = el('div', 'stat');
    speedBox.innerHTML = '<div class="stat-label">Speed</div>';
    this.speed = el('div', 'stat-value', '0.0<small> kn</small>');
    speedBox.appendChild(this.speed);
    row.appendChild(speedBox);
    const heelBox = el('div', 'stat');
    heelBox.innerHTML = '<div class="stat-label">Heel</div>';
    this.heel = el('div', 'stat-value', '0°');
    heelBox.appendChild(this.heel);
    row.appendChild(heelBox);
    // Wind rose: true wind (brass) and apparent wind (pale), relative to the bow.
    const windBox = el('div', 'wind');
    windBox.innerHTML = `
      <svg viewBox="-50 -50 100 100" class="wind-rose">
        <circle r="44" class="rose-ring"/>
        <path d="M0,-30 L7,18 L0,12 L-7,18 Z" class="rose-ship"/>
        <g class="rose-true"><line x1="0" y1="-44" x2="0" y2="-22"/><path d="M0,-22 L-5,-31 L5,-31 Z"/></g>
        <g class="rose-app"><line x1="0" y1="-44" x2="0" y2="-26"/></g>
      </svg>`;
    this.windArrow = windBox.querySelector('.rose-true')!;
    this.appArrow = windBox.querySelector('.rose-app')!;
    this.windText = el('div', 'wind-text');
    windBox.appendChild(this.windText);
    row.appendChild(windBox);
    helm.appendChild(row);
    const sails = el('div', 'sails');
    this.sailName = el('div', 'sail-name');
    sails.appendChild(this.sailName);
    const bars = el('div', 'sail-bars');
    for (let i = 0; i < SAIL_PLANS.length - 1; i++) {
      const b = el('span');
      bars.appendChild(b);
      this.sailBars.push(b);
    }
    sails.appendChild(bars);
    this.trim = el('div', 'trim');
    sails.appendChild(this.trim);
    helm.appendChild(sails);
    const rud = el('div', 'rudder');
    rud.innerHTML = '<span class="rudder-label">Port</span><div class="rudder-track"><div class="rudder-mid"></div></div><span class="rudder-label">Stbd</span>';
    this.rudder = el('div', 'rudder-pip');
    rud.querySelector('.rudder-track')!.appendChild(this.rudder);
    helm.appendChild(rud);
    this.hudWrap.appendChild(helm);

    // --- the fight: guns, damage, contacts, log (bottom right and around)
    this.tracker = new Tracker(this.hudWrap, world, (id) => actions.track(id));
    this.combat = new CombatHud(this.hudWrap, root, world, this.tracker, () => actions.restart());

    // --- top left: time and weather
    const top = el('div', 'panel topleft');
    this.clock = el('div', 'clock');
    this.weather = el('div', 'weather-line');
    top.appendChild(this.clock);
    top.appendChild(this.weather);
    this.hudWrap.appendChild(top);

    // --- top right: camera
    const camBox = el('div', 'panel topright');
    this.camName = el('div', 'cam-name');
    this.shotName = el('div', 'shot-name');
    this.fps = el('div', 'fps');
    camBox.appendChild(this.camName);
    camBox.appendChild(this.shotName);
    camBox.appendChild(this.fps);
    this.hudWrap.appendChild(camBox);

    // --- help
    this.help = el('div', 'panel help hidden');
    this.help.innerHTML = `
      <h2>At the helm</h2>
      <table>
        <tr><td>A / D, ← / →</td><td>Put the helm over (returns amidships when released)</td></tr>
        <tr><td>W / S, ↑ / ↓</td><td>Make sail / shorten sail (5 sail plans)</td></tr>
        <tr><td>Q / E</td><td>Fire the port / starboard broadside (the crews lay the guns)</td></tr>
        <tr><td>G</td><td>Change the shot the crews load next: round, chain, grape, shell</td></tr>
        <tr><td>B</td><td>Throw grapnels and board a ship alongside (match her speed)</td></tr>
        <tr><td>L</td><td>Show or douse our lights (run dark at night)</td></tr>
        <tr><td>8, Y</td><td>Battle view · cycle the ship it follows</td></tr>
        <tr><td>Click a ship in sight</td><td>Keep a glass on her: bearing, distance, course, speed</td></tr>
        <tr><td>T</td><td>Automatic sail trim on / off</td></tr>
        <tr><td>Z / X</td><td>Brace yards / ease sheets (manual trim)</td></tr>
        <tr><td>1 – 8, C</td><td>Cameras: third person, free, on deck, helm, cinematic, wide, details, battle</td></tr>
        <tr><td>Mouse drag, wheel</td><td>Look around / zoom (third person)</td></tr>
        <tr><td>WASD, R/F, Shift</td><td>Fly (free camera) · walk (on deck)</td></tr>
        <tr><td>[ / ]</td><td>Time of day back / forward an hour</td></tr>
        <tr><td>N</td><td>Next weather</td></tr>
        <tr><td>Tab</td><td>Weather, sea, graphics and sound settings</td></tr>
        <tr><td>V</td><td>Hide the HUD</td></tr>
        <tr><td>M</td><td>Mute</td></tr>
      </table>`;
    root.appendChild(this.help);

    // --- settings
    this.panel = el('div', 'panel settings hidden');
    this.buildSettings();
    root.appendChild(this.panel);
  }

  private buildSettings(): void {
    const p = this.panel;
    p.appendChild(el('h2', '', 'Conditions'));
    const wx = el('div', 'btnrow');
    for (const k of WEATHER_KINDS) {
      const b = el('button', 'wx', WEATHER_NAMES[k]);
      b.dataset.kind = k;
      b.addEventListener('click', () => this.actions.setWeather(k));
      wx.appendChild(b);
    }
    p.appendChild(wx);
    p.appendChild(this.check('dynamic', 'Weather changes on its own', true, (v) => this.actions.setDynamicWeather(v)));
    p.appendChild(this.slider('hours', 'Time of day', 0, 24, 0.1, this.world.day.hours, (v) => this.actions.setHours(v), (v) => fmtTime(v)));
    p.appendChild(this.select('speed', 'Game speed (everything)', [['0', 'Paused'], ['0.25', '¼×'], ['0.5', '½×'], ['1', 'Normal'], ['2', '2×'], ['4', '4×'], ['8', '8×']], '1', (v) => this.actions.setGameSpeed(Number(v))));
    p.appendChild(this.select('timescale', 'Day clock', [['0', 'Stopped'], ['1', 'Real time'], ['20', 'Brisk (×20)'], ['120', 'Fast (×120)'], ['600', 'Very fast (×600)']], '20', (v) => this.actions.setTimeScale(Number(v))));
    p.appendChild(this.slider('waves', 'Wave intensity', 0.2, 2, 0.05, 1, (v) => this.actions.setWaveIntensity(v), (v) => `${Math.round(v * 100)}%`));
    p.appendChild(this.slider('wind', 'Wind (0 = follow weather)', 0, 28, 0.5, 0, (v) => this.actions.setWind(v <= 0 ? null : v), (v) => (v <= 0 ? 'Weather' : `${Math.round(v * 1.944)} kn`)));
    p.appendChild(el('h2', '', 'Other ships'));
    p.appendChild(this.check('enemies', 'Ships at sea (patrols, pirates, convoys)', this.world.encounters.enabled, (v) => this.actions.setEnemies(v)));
    p.appendChild(this.select('difficulty', 'Captains', [['easy', 'Green'], ['normal', 'Seasoned'], ['hard', 'Veteran'], ['legendary', 'Legendary']], this.world.encounters.difficulty, (v) => this.actions.setDifficulty(v as DifficultyId)));
    const spawn = el('div', 'btnrow');
    for (const [k, label] of [['lone_raider', 'Raider'], ['hunters', 'Hunting pair'], ['convoy', 'Convoy'], ['rival', 'Rival frigate'], ['squadron', 'Navy squadron']] as const) {
      const b = el('button', 'spawn', label);
      b.addEventListener('click', () => this.actions.spawn(k));
      spawn.appendChild(b);
    }
    p.appendChild(spawn);
    p.appendChild(el('h2', '', 'Picture'));
    p.appendChild(this.select('quality', 'Quality', Object.entries(QUALITY).map(([k, q]) => [k, q.name]), 'high', (v) => this.actions.setQuality(v)));
    const post = el('div', 'checks');
    for (const [k, label] of [['bloom', 'Bloom'], ['dof', 'Depth of field'], ['motionBlur', 'Motion blur'], ['godRays', 'Sun shafts'], ['grain', 'Film grain']] as const) {
      post.appendChild(this.check(k, label, true, (v) => this.actions.setPost(k, v)));
    }
    p.appendChild(post);
    p.appendChild(el('h2', '', 'Sound & controls'));
    p.appendChild(this.slider('volume', 'Volume', 0, 1, 0.01, 0.8, (v) => this.actions.setVolume(v), (v) => `${Math.round(v * 100)}%`));
    p.appendChild(this.slider('sens', 'Mouse sensitivity', 0.2, 2.5, 0.05, 1, (v) => this.actions.setSensitivity(v), (v) => v.toFixed(2)));
    const cams = el('div', 'btnrow');
    for (const c of CAMERA_MODES) {
      const b = el('button', 'cam', `${c.key} · ${c.name}`);
      b.addEventListener('click', () => this.actions.setCamera(c.id));
      cams.appendChild(b);
    }
    p.appendChild(cams);
  }

  private slider(key: string, label: string, min: number, max: number, step: number, value: number, on: (v: number) => void, fmt: (v: number) => string): HTMLElement {
    const row = el('label', 'row');
    const l = el('span', 'row-label', label);
    const out = el('span', 'row-value', fmt(value));
    const input = el('input');
    input.type = 'range';
    input.min = String(min);
    input.max = String(max);
    input.step = String(step);
    input.value = String(value);
    input.addEventListener('input', () => {
      const v = Number(input.value);
      out.textContent = fmt(v);
      on(v);
    });
    this.controls[key] = input;
    row.append(l, input, out);
    return row;
  }

  private check(key: string, label: string, value: boolean, on: (v: boolean) => void): HTMLElement {
    const row = el('label', 'check');
    const input = el('input');
    input.type = 'checkbox';
    input.checked = value;
    input.addEventListener('change', () => on(input.checked));
    this.controls[key] = input;
    row.append(input, el('span', '', label));
    return row;
  }

  private select(key: string, label: string, options: [string, string][], value: string, on: (v: string) => void): HTMLElement {
    const row = el('label', 'row');
    const s = el('select');
    for (const [v, t] of options) {
      const o = el('option', '', t);
      o.value = v;
      s.appendChild(o);
    }
    s.value = value;
    s.addEventListener('change', () => on(s.value));
    this.controls[key] = s;
    row.append(el('span', 'row-label', label), s);
    return row;
  }

  setStatus(text: string, ready: boolean): void {
    this.status.textContent = text;
    (this.title.querySelector('.title-go') as HTMLButtonElement).disabled = !ready;
  }

  toggleHelp(): void {
    this.help.classList.toggle('hidden');
  }
  toggleSettings(): boolean {
    this.panel.classList.toggle('hidden');
    return !this.panel.classList.contains('hidden');
  }
  toggleHud(): void {
    this.hidden = !this.hidden;
    this.hudWrap.classList.toggle('hidden', this.hidden);
  }

  update(camMode: CameraMode, shot: string, fps: number, renderScale: number): void {
    const w = this.world;
    const s = w.ship;
    const hdg = (s.heading * 180) / Math.PI;
    this.compassTape.style.transform = `translateX(${-(hdg + 360) * 3 + 150}px)`;
    this.speed.innerHTML = `${(Math.max(0, s.speed) * 1.944).toFixed(1)}<small> kn</small>`;
    const heelDeg = (s.heel * 180) / Math.PI;
    this.heel.textContent = `${Math.abs(heelDeg).toFixed(0)}° ${Math.abs(heelDeg) < 0.5 ? '' : heelDeg > 0 ? 'stbd' : 'port'}`;
    // Wind from, relative to the bow (clockwise).
    const rel = wrapAngle(w.wind.fromBearing - s.heading);
    this.windArrow.setAttribute('transform', `rotate(${(rel * 180) / Math.PI})`);
    const aw = s.apparentWind;
    const appFrom = Math.atan2(-aw.z, -aw.x);
    this.appArrow.setAttribute('transform', `rotate(${(appFrom * 180) / Math.PI})`);
    const kn = w.wind.speed * 1.944;
    let bf = 0;
    while (bf < BEAUFORT.length && w.wind.speed > BEAUFORT[bf]!) bf++;
    this.windText.innerHTML = `${kn.toFixed(0)} kn<br><span>${BEAUFORT_NAMES[bf]}</span>`;
    this.sailName.textContent = s.sails.planName;
    this.sailBars.forEach((b, i) => b.classList.toggle('on', i < s.sails.plan));
    this.trim.textContent = s.sails.autoTrim ? 'Trim: auto' : 'Trim: manual';
    this.rudder.style.left = `${50 + (s.rudder / 0.62) * 48}%`;
    this.combat.update(1 / Math.max(1, fps));
    this.clock.textContent = fmtTime(w.day.hours);
    const hs = w.waves.significantHeight;
    const speed = w.speed === 1 ? '' : w.speed === 0 ? ' · paused' : ` · ${w.speed}× speed`;
    this.weather.textContent = `${WEATHER_NAMES[w.weather.kind]}${w.weather.transition < 1 ? ' (changing)' : ''} · seas ${hs.toFixed(1)} m${speed}`;
    this.camName.textContent = CAMERA_MODES.find((c) => c.id === camMode)?.name ?? '';
    this.shotName.textContent = shot;
    this.fps.textContent = `${fps.toFixed(0)} fps · ${Math.round(renderScale * 100)}%`;
    const h = this.controls.hours as HTMLInputElement | undefined;
    if (h && document.activeElement !== h) h.value = String(w.day.hours);
    for (const b of this.panel.querySelectorAll<HTMLButtonElement>('button.wx')) b.classList.toggle('active', b.dataset.kind === w.weather.kind);
  }

  handle(events: SimEvent[]): void {
    this.combat.handle(events);
  }
}

function fmtTime(h: number): string {
  const hh = Math.floor(h) % 24;
  const mm = Math.floor((h - Math.floor(h)) * 60);
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}
